import colorPallete, { bodyFonts, elevation, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { getPagesByParent } from "@insite/client-framework/Services/ContentService";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import styled, { ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    overrideTitle?:string;
    id?:string;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        parentCategoryName?: string;
        defaultLanguageId?:any;
        currentLanguageId?:any;
        parentNodeId?:string;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<MenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${elevation.dp08};
    width: ${getProp("width", 303)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;
    top: calc(100% + 20px);
    padding: 16px 0 24px 0;
    border: 1px solid ${colorPallete.grey300};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    &.d-none {
        display: none;
    }

    &.d-block {
        display: block;
    }

    .parent-title {
        color: ${colorPallete.grey800};
        padding: 10px 40px 18px;
        position: relative;
        font-size: ${bodyFonts.body2SemiBoldLg.fontSize};
        line-height: ${bodyFonts.body2SemiBoldLg.lineHeight};
        font-weight: ${bodyFonts.body2SemiBoldLg.fontWeight};
        margin-bottom: 8px;

        &:empty {
            display: none;
        }

        &::after {
            content: "";
            position: absolute;
            bottom: 0;
            height: 1px;
            background: ${colorPallete.grey300};
            width: calc(100% - 80px);
            left: 40px;
        }
    }

    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
                height: 100%;
            }
    `}

    &.menu-active {
        > span {
            [class*="TypographyStyle"] {
                color: ${colorPallete.primaryRed500};
            }
        }
        [class*="IconWrapper-"]:not(.siginUserIcon) {
            transform: rotate(180deg);
            svg {
                color: ${colorPallete.primaryRed500};
            }
        }
    }
    ${injectCss}
`;

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    &:focus-within > ${MenuStyle} {
        display: block;
        left: 296px;
        border-bottom-left-radius: 0;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: 296px;
        border-bottom-left-radius: 0;
        top: -1px;
    }
    &.child-menu-item {
        a,
        a span {
            font-weight: ${themeFontWeights.regular};
        }
    }
    a {
        position: relative;
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        &:hover {
            background: ${colorPallete.grey100};
            &::before {
                content: "";
                position: absolute;
                left: 0;
                top: 0;
                width: 4px;
                height: 100%;
                background: ${colorPallete.primaryRed500};
            }
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: 100%;"}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
    menuLinks:MappedLink[]|undefined;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();
    private openerElement: HTMLElement | null = null;

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 303,
    };

    state: MenuState = {
        canBeOpen: false,
        isMobile: false,
        shouldRender: true,
        menuLinks:this.props.menuItems,
    };

    componentDidMount() {

        let customLinks:MappedLink[] | undefined;
        customLinks = { ...this.props.menuItems };
        const languageKey = this.props.currentLanguageId ?? this.props.defaultLanguageId;
        customLinks =  this.updateCustomPageTitle(this.props.menuItems,languageKey);
        
        this.setState({ menuLinks: customLinks });

        document.addEventListener("click", this.globalClickListener);
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        window.addEventListener("popstate", this.closeDropdown);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener("click", this.globalClickListener);

        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });

        window.removeEventListener("popstate", this.closeDropdown);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        const triggerButton = this.menuWrapperRef.current?.querySelector('.trigger') as HTMLElement;
        const focusedElement = document.activeElement as HTMLElement;
        const menuWrapper = this.menuWrapperRef.current;

        if (e.key === 'Escape') {
            this.closeDropdown(); 

            // Focus back to the parent that opened the dropdown
            if (this.openerElement) {
                this.openerElement.focus();
                this.openerElement = null; // reset after use
            }
        } else if (e.key === 'Enter') {
            // Only trigger click if top-level menu trigger is focused
            if (focusedElement === triggerButton) {
                e.preventDefault(); // Prevent default link navigation if any
                this.openerElement = triggerButton;
                triggerButton.click(); // Simulate click to open the menu
            }
        } 
        else if (e.key === 'Tab') {
            this.trapFocus(e);
            const triggerButton = this.menuWrapperRef.current?.querySelector(
                'ul li:nth-child(2) a'
            ) as HTMLElement; 
          if(triggerButton.classList.contains('subMenuList')) {
            triggerButton.focus();
            triggerButton.classList.remove('subMenuList');
          }
        }
    };
    
    trapFocus = (e: KeyboardEvent) => {
        const menuWrapper = this.menuWrapperRef.current;
    
        if (!menuWrapper) {
            return;
        }
    
        const focusedElement = document.activeElement as HTMLElement;
        const currentSubMenu = focusedElement.closest('.menu-dropdown, .account-dropdown');
    
        if (!currentSubMenu) {
            return;
        }
    
        const focusableElements = currentSubMenu.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
       
        if (!focusableElements || focusableElements.length === 0) {
            return;
        } 

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
    
        
        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    };
    
    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };
    updateCustomPageTitle = (staticPagelink:MappedLink[],languageId:any) => {
        let childOverrideTitle : MappedLink[]=[];
        if(staticPagelink)
        {
            
            if(this.props.parentNodeId)
            {
                const tempParameter = { parentNodeId: this.props.parentNodeId };
                let dsgCustomPages: PageModel[];
                 getPagesByParent(tempParameter).then(result =>{
                    dsgCustomPages = result?.pages;
                    if(dsgCustomPages)
                    {
                        staticPagelink.forEach((child) => {
                            if(child.type === "DsgCustomPage" && child.url)
                            { 
                                const childLink:MappedLink | undefined = { ...child };
                                let page;
                                    if(dsgCustomPages && dsgCustomPages.filter(x=>x.nodeId === child.id))
                                    {
                                        page = dsgCustomPages.filter(x=>x.nodeId === child.id)[0];
                                    }
                                  
                                    if(page && page.translatableFields && page?.translatableFields["overrideTitle"] && page?.translatableFields["overrideTitle"][languageId] && childLink)
                                    {
                                        childLink.title = page?.translatableFields["overrideTitle"][languageId];
                                        childOverrideTitle.push(childLink);
                                    }
                                    else
                                    {
                                        childOverrideTitle.push(child);
                                    }
                            }
                            else
                            {
                                childOverrideTitle.push(child);
                            }
                        });
                    }
                });
            }
            else
            {
                childOverrideTitle = staticPagelink;
            }
           
        }
        return childOverrideTitle;
    }

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    closeDropdown = () => {
        const dropdown = document.querySelector('.current-active-dropdown');
        const activeMenu = document.querySelector('.menu-active');
        dropdown?.classList.remove('current-active-dropdown');
        dropdown?.classList.remove('d-block');
        activeMenu?.classList.remove('menu-active');
        const triggerButton = activeMenu?.querySelector('.trigger');
        if (triggerButton) {
            triggerButton.setAttribute('aria-expanded', 'false'); 
        }
    }

    globalClickListener = () => {
        this.closeDropdown();
        this.setState({ canBeOpen: false }, () => {
            window.removeEventListener("click", this.globalClickListener);
        });
    };

    handleMenuExpand = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        const triggerButton = this.menuWrapperRef.current?.querySelector('.trigger') as HTMLElement;
        const focusedElement = document.activeElement as HTMLElement;
        const submenu = this.menuWrapperRef.current?.querySelector('ul li:nth-child(2) > a') as HTMLElement;
        const submenuNew = this.menuWrapperRef.current?.querySelector('ul li:nth-child(2) > a') as HTMLElement;   
        if(this.menuWrapperRef.current?.classList?.contains('cascading-menu')) {

            const firstLevelMenuItems: HTMLCollection = this.menuWrapperRef.current?.closest('.main-nav-hidden')?.children as HTMLCollection;
            Array.from(firstLevelMenuItems).forEach((item) => {
                const itemNav = item.querySelector('nav');
                const triggerButton = item.querySelector('.trigger');
                if (itemNav !== this.menuWrapperRef.current) {
                    itemNav?.querySelector('ul')?.classList.add('d-none');
                    itemNav?.querySelector('ul')?.classList.remove('d-block');
                    itemNav?.classList.remove("menu-active");
                    if (triggerButton) {
                        triggerButton.setAttribute('aria-expanded', 'false'); 
                    }
                }

                if (itemNav === this.menuWrapperRef.current) {
                    if (triggerButton) {
                        const expanded = triggerButton.getAttribute('aria-expanded') === 'true'; // Check current state
                        triggerButton.setAttribute('aria-expanded', expanded ? 'false' : 'true'); // Toggle aria-expanded
                    }
                    setTimeout(() => {
                        itemNav?.classList.toggle("menu-active");
                        itemNav?.querySelector('ul')?.classList.toggle('d-none')
                        itemNav?.querySelector('ul')?.classList.toggle('d-block');
                        itemNav?.querySelector('ul')?.classList.add('current-active-dropdown', 'menu-dropdown');
                        document.querySelector('.account-dropdown')?.classList.add('d-none');
                        document.querySelector('.account-dropdown')?.classList.remove('d-block');
                        document.querySelector('.accountMenu ')?.classList.remove('menu-active');
                     
                        if(submenuNew) {
                           // submenuNew.focus();
                        }
                    }, 100);
                    
                }
            });
        }
        
        if (this.menuWrapperRef.current?.classList?.contains('accountMenu')) {
            const triggerButton = this.menuWrapperRef.current.querySelector('.trigger');
            if (triggerButton) {
                const expanded = triggerButton.getAttribute('aria-expanded') === 'true';
                triggerButton.setAttribute('aria-expanded', expanded ? 'false' : 'true'); 
            }
            setTimeout(() => {
                if(this.menuWrapperRef.current) {
                    this.menuWrapperRef.current.querySelector('ul')?.classList.toggle('d-block');
                    this.menuWrapperRef.current?.classList.toggle("menu-active");
                    this.menuWrapperRef.current?.querySelector('ul')?.classList.add('current-active-dropdown', 'account-dropdown');
                    document.querySelector('.menu-dropdown')?.classList.remove('d-block');
                    document.querySelector('.menu-dropdown')?.classList.add('d-none');
                    document.querySelector('.menu-dropdown')?.closest('.cascading-menu')?.classList.remove('menu-active');
                    if(submenuNew) {
                        submenuNew.focus();
                    } 
                }
            }, 100);
        }

        this.setState(
            prevState => ({ canBeOpen: !prevState.canBeOpen }),
            () => {
                if (this.state.canBeOpen) {
                    window.addEventListener("click", this.globalClickListener);
                }
            },
        );
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, mergeCss, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                        parentTitle: string,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        return (
                            <MenuStyle
                                width={otherProps.width}
                                css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                            >
                                <p className="parent-title">
                                    {parentTitle && parentTitle !== "" ? parentTitle : otherProps.parentCategoryName}
                                </p>
                                {childMenuItems?.filter(item => !item.excludeFromNavigation)
                                    .map((item, index) => {
                                        const hasChildren =
                                            item.children?.some(o => !o.excludeFromNavigation) &&
                                            currentDepth < maxDepth;

                                        if (
                                            (item.type === "ChangeCustomerPage" &&
                                                !this.props.displayChangeCustomerLink) ||
                                            !this.state.shouldRender
                                        ) {
                                            return null;
                                        }

                                        return (
                                            <MenuItemStyle
                                                className={hasChildren ? "child-menu-item" : ""}
                                                width={otherProps.width}
                                                css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                                key={`${item.title}_${index}`}
                                                data-test-selector="menuItem"
                                                onClick={() => {
                                                    this.closeMenuWithoutChildren(hasChildren);
                                                }}
                                            >
                                                <Clickable
                                                    href={item.url}
                                                    onClick={this.closeMenu}
                                                    {...applyProp("menuItemClickableProps")}
                                                    {...item.clickableProps}
                                                >
                                                    <MenuItemText
                                                        hasChildren={hasChildren}
                                                        {...applyProp("menuItemTypographyProps")}
                                                        data-test-selector="menuItemText"
                                                    >
                                                        {item.title}
                                                    </MenuItemText>
                                                </Clickable>
                                                {hasChildren
                                                    ? renderMenuItems(item!.children!, currentDepth + 1, item!.title)
                                                    : null}
                                            </MenuItemStyle>
                                        );
                                    })}
                            </MenuStyle>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <MenuWrapper
                                ref={this.menuWrapperRef}
                                canBeOpen={this.state.canBeOpen}
                                onClick={this.handleMenuExpand}
                                isOpen={isOpen}
                                onMouseLeave={this.menuWrapperMouseLeaveHandler}
                                css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger", "aria-expanded": isOpen ? true : false, role: "button" })}
                                {renderMenuItems(this.state.menuLinks!, 1, "")}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(Menu);

export { MenuItemText };
